import React, { useState, useEffect } from 'react';
import { Settings, Plus, Trash2, Save, Image as ImageIcon } from 'lucide-react';
import api from '../../services/api';
import { Button, Input, Select } from '../ui';

interface MasterConfig {
  id: number;
  key: string;
  value: string;
  field_type: 'Text Field' | 'Text Area' | 'File Upload' | 'Dropdown';
  dropdown_options: string[];
}

interface Draft {
  value: string;
  optionsCsv: string;
}

const FIELD_TYPES = ['Text Field', 'Text Area', 'File Upload', 'Dropdown'] as const;
const MAX_FILE_SIZE_BYTES = 1 * 1024 * 1024;
const ALLOWED_FILE_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

const textareaClasses =
  'w-full bg-white border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed px-4 py-3 text-base rounded-xl';

// PNG/JPEG magic-byte prefixes as they appear in base64, so an uploaded
// image can be previewed without trusting/storing a separate mime type
// (matches the DB, which only ever stores the raw base64 string).
const guessImageMime = (base64: string): string => {
  if (base64.startsWith('iVBORw0KGgo')) return 'image/png';
  if (base64.startsWith('/9j/')) return 'image/jpeg';
  return 'image/png';
};

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      reject(new Error('File too large. Max 1 MB allowed.'));
      return;
    }
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      reject(new Error('Unsupported file type. PNG or JPG only.'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1] || ''); // strip the data: URL prefix, keep raw base64
    };
    reader.onerror = () => reject(new Error('Failed to read file.'));
    reader.readAsDataURL(file);
  });

const MasterConfigurations: React.FC = () => {
  const [configs, setConfigs] = useState<MasterConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [savingId, setSavingId] = useState<number | null>(null);
  const [error, setError] = useState('');

  const [newKey, setNewKey] = useState('');
  const [newFieldType, setNewFieldType] = useState<string>('Text Field');
  const [newValue, setNewValue] = useState('');
  const [newOptionsCsv, setNewOptionsCsv] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConfigs = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/master-config/');
      if (res.data.success) {
        const data: MasterConfig[] = res.data.data;
        setConfigs(data);
        const nextDrafts: Record<number, Draft> = {};
        data.forEach(c => {
          nextDrafts[c.id] = { value: c.value, optionsCsv: (c.dropdown_options || []).join(',') };
        });
        setDrafts(nextDrafts);
      }
    } catch (err) {
      console.error('Error loading master configurations:', err);
      setError('Failed to load configurations.');
    } finally {
      setLoading(false);
    }
  };

  const updateDraft = (id: number, patch: Partial<Draft>) => {
    setDrafts(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  };

  const resetNewForm = () => {
    setNewKey('');
    setNewFieldType('Text Field');
    setNewValue('');
    setNewOptionsCsv('');
  };

  const handleCreate = async () => {
    const key = newKey.trim();
    if (!key) {
      setError('Key cannot be empty.');
      return;
    }
    setCreating(true);
    setError('');
    try {
      const dropdown_options =
        newFieldType === 'Dropdown'
          ? newOptionsCsv.split(',').map(o => o.trim()).filter(Boolean)
          : undefined;
      const res = await api.post('/master-config/', {
        key,
        value: newValue,
        field_type: newFieldType,
        dropdown_options,
      });
      if (res.data.success) {
        resetNewForm();
        await loadConfigs();
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to create configuration.');
    } finally {
      setCreating(false);
    }
  };

  const handleNewFile = async (file: File) => {
    try {
      setNewValue(await readFileAsBase64(file));
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to read file.');
    }
  };

  const handleRowFile = async (config: MasterConfig, file: File) => {
    try {
      const base64 = await readFileAsBase64(file);
      updateDraft(config.id, { value: base64 });
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to read file.');
    }
  };

  const handleUpdate = async (config: MasterConfig) => {
    const draft = drafts[config.id];
    if (!draft) return;
    setSavingId(config.id);
    setError('');
    try {
      const dropdown_options =
        config.field_type === 'Dropdown'
          ? draft.optionsCsv.split(',').map(o => o.trim()).filter(Boolean)
          : undefined;
      const res = await api.put(`/master-config/${encodeURIComponent(config.key)}`, {
        value: draft.value,
        field_type: config.field_type,
        dropdown_options,
      });
      if (res.data.success) await loadConfigs();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to update configuration.');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (config: MasterConfig) => {
    if (!window.confirm(`Delete configuration "${config.key}"? This cannot be undone.`)) return;
    setSavingId(config.id);
    setError('');
    try {
      await api.delete(`/master-config/${config.id}`);
      await loadConfigs();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to delete configuration.');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-2">
        <Settings className="w-6 h-6 text-blue-600" />
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Master Configurations</h1>
      </div>
      <p className="text-sm text-gray-500 -mt-4">
        GST, branding, FX and itinerary defaults used across the app. Admin only.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">{error}</div>
      )}

      {/* Add New Configuration */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 space-y-4">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add New Configuration
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Key</label>
            <Input size="sm" value={newKey} onChange={e => setNewKey(e.target.value)} placeholder="e.g. GST_RATE" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
            <Select
              value={newFieldType}
              onChange={e => { setNewFieldType(e.target.value); setNewValue(''); setNewOptionsCsv(''); }}
              className="w-full"
            >
              {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </Select>
          </div>
        </div>

        {newFieldType === 'Dropdown' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Dropdown Options (comma separated)</label>
            <Input size="sm" value={newOptionsCsv} onChange={e => setNewOptionsCsv(e.target.value)} placeholder="Percentage, Per Pax" />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
          {newFieldType === 'Text Area' && (
            <textarea className={textareaClasses} rows={3} value={newValue} onChange={e => setNewValue(e.target.value)} />
          )}
          {newFieldType === 'Text Field' && (
            <Input size="sm" value={newValue} onChange={e => setNewValue(e.target.value)} />
          )}
          {newFieldType === 'Dropdown' && (
            (() => {
              const options = newOptionsCsv.split(',').map(o => o.trim()).filter(Boolean);
              return options.length > 0 ? (
                <Select value={newValue} onChange={e => setNewValue(e.target.value)} className="w-full">
                  <option value="">Select default value</option>
                  {options.map(o => <option key={o} value={o}>{o}</option>)}
                </Select>
              ) : (
                <p className="text-xs text-gray-400 italic">Enter dropdown options above first.</p>
              );
            })()
          )}
          {newFieldType === 'File Upload' && (
            <div className="space-y-2">
              <input
                type="file"
                accept=".png,.jpg,.jpeg"
                onChange={e => { const f = e.target.files?.[0]; if (f) handleNewFile(f); }}
                className="block text-sm"
              />
              {newValue && (
                <img src={`data:${guessImageMime(newValue)};base64,${newValue}`} alt="preview" className="h-16 rounded border border-gray-200" />
              )}
            </div>
          )}
        </div>

        <Button onClick={handleCreate} disabled={creating} icon={Save} size="sm">
          {creating ? 'Saving...' : 'Add Configuration'}
        </Button>
      </div>

      {/* Existing Configurations */}
      <div className="space-y-3">
        <h2 className="font-semibold text-gray-900">Existing Configurations</h2>

        {loading ? (
          <div className="text-center py-8 text-gray-500 text-sm">Loading...</div>
        ) : configs.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm italic bg-white border border-gray-200 rounded-xl">
            No configuration added yet.
          </div>
        ) : (
          configs.map(config => {
            const draft = drafts[config.id] || { value: config.value, optionsCsv: (config.dropdown_options || []).join(',') };
            const dropdownOptions = draft.optionsCsv.split(',').map(o => o.trim()).filter(Boolean);
            const isSaving = savingId === config.id;
            return (
              <div key={config.id} className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-semibold text-gray-900">🔧 {config.key}</h3>
                  <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                    {config.field_type}
                  </span>
                </div>

                {config.field_type === 'Text Field' && (
                  <Input size="sm" value={draft.value} onChange={e => updateDraft(config.id, { value: e.target.value })} />
                )}

                {config.field_type === 'Text Area' && (
                  <textarea
                    className={textareaClasses}
                    rows={3}
                    value={draft.value}
                    onChange={e => updateDraft(config.id, { value: e.target.value })}
                  />
                )}

                {config.field_type === 'Dropdown' && (
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Options (comma separated)</label>
                      <Input
                        size="sm"
                        value={draft.optionsCsv}
                        onChange={e => updateDraft(config.id, { optionsCsv: e.target.value })}
                      />
                    </div>
                    {dropdownOptions.length > 0 ? (
                      <Select value={draft.value} onChange={e => updateDraft(config.id, { value: e.target.value })} className="w-full">
                        {!dropdownOptions.includes(draft.value) && draft.value && (
                          <option value={draft.value}>{draft.value} (current)</option>
                        )}
                        {dropdownOptions.map(o => <option key={o} value={o}>{o}</option>)}
                      </Select>
                    ) : (
                      <p className="text-xs text-amber-600 italic">No options configured for this dropdown.</p>
                    )}
                  </div>
                )}

                {config.field_type === 'File Upload' && (
                  <div className="space-y-2">
                    {draft.value ? (
                      <img
                        src={`data:${guessImageMime(draft.value)};base64,${draft.value}`}
                        alt={config.key}
                        className="h-20 rounded border border-gray-200"
                      />
                    ) : (
                      <div className="h-20 w-20 flex items-center justify-center rounded border border-dashed border-gray-300 text-gray-300">
                        <ImageIcon className="w-6 h-6" />
                      </div>
                    )}
                    <input
                      type="file"
                      accept=".png,.jpg,.jpeg"
                      onChange={e => { const f = e.target.files?.[0]; if (f) handleRowFile(config, f); }}
                      className="block text-sm"
                    />
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-1">
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={Trash2}
                    disabled={isSaving}
                    onClick={() => handleDelete(config)}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    Delete
                  </Button>
                  <Button size="sm" icon={Save} disabled={isSaving} onClick={() => handleUpdate(config)}>
                    {isSaving ? 'Saving...' : 'Update'}
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default MasterConfigurations;
